import { useState, useCallback, useMemo, useRef } from 'react';
// Custom hooks
import usePrevious from './usePrevious';
// Sub-components
import NodesLabels from './NodesLabels';
import TripRibbons from './TripRibbons';
import TripCaption from './TripCaption';
import PersonCaption from './PersonCaption';
import TripsBar from './TripsBar';
import HammockDiaryLink from './HammockDiaryLink';
// Tools
import { no_propagate } from './utils';

const MARGIN = 100;

const ChordDiagram = ({ width, height, orient, centre_x, centre_y, order_buttons_margin, radius_off_screen, data_names, trip_dates, trip_key_memories, data_binary, hammock_diary_urls }) => {

  // ***************************************************************************
  // * Ordering data
  // ***************************************************************************
  const first_trip_data = useMemo(() => {
    // Find index of first trip for each person
    let data = [];
    for (let i = 0; i < data_binary[0].length; i++) {
      // Find first trip
      for (let j = 0; j < data_binary.length; j++) {
        if (data_binary[j][i]) {
          data[i] = j;
          break;
        }
      }
    }
    return data;
  }, [data_binary]);

  const num_trips_data = useMemo(() => {
    // Find num trips for each person
    let data = [];
    for (let i = 0; i < data_binary[0].length; i++) {
      // Count num trips. Negative since we want to sort descending
      data[i] = 0;
      for (let j = 0; j < data_binary.length; j++) {
        data[i] -= (data_binary[j][i] !== 0);
      }
    }
    return data;
  }, [data_binary]);

  const num_covoyagers_data = useMemo(() => {
    // Find num covoyagers for each person
    let data = [];
    for (let i = 0; i < data_binary[0].length; i++) {
      // Collect num covoyagers
      let covoyagers_info = {};
      for (let j = 0; j < data_binary.length; j++) {
        if (data_binary[j][i]) {
          // Person i was on trip j so get others on that trip
          for (let k = 0; k < data_binary[j].length; k++) {
            if (data_binary[j][k]) {
              if (k === i) {
                covoyagers_info[k] = data_binary[j][k] - 1;
              } else {
                covoyagers_info[k] = data_binary[j][k];
              }
            }
          }
        }
      }
      // Sum up group size across all covoyagers
      // Negate since we want to sort descending
      data[i] = -Object.values(covoyagers_info).reduce((a, b) => a + b, 0);
    }
    return data
  }, [data_binary]);

  // Helper function for sorting people by various sorting data
  const get_people_ordering = useCallback((list_of_sorting_data, start_from = null) => {
    let person_indices;
    if (start_from === null) {
      // Write down all person indices (0 to n-1)
      person_indices = [...Array(data_binary[0].length).keys()];
    } else {
      person_indices = [...start_from];
    }
    // Sort according to each sorting data in turn. Reverse since list_of_sorting_data
    // is in priority order, so the zeroth entry should be applied last.
    for (let i = list_of_sorting_data.length - 1; i >= 0; i--) {
      const sorting_data = list_of_sorting_data[i];
      person_indices.sort((a, b) => sorting_data[a] - sorting_data[b]);
    }
    return person_indices;
  }, [data_binary]);

  // The default sorting is by first_trip, num_trips, num_covoyagers (in that order of priority).
  const default_people_sorting = useMemo(() => get_people_ordering([first_trip_data, num_trips_data, num_covoyagers_data]),
    [get_people_ordering, first_trip_data, num_trips_data, num_covoyagers_data]);

  // ***************************************************************************
  // * State variables
  // ***************************************************************************
  const [focused_person, _set_focused_person] = useState(null);
  const [focused_trip, _set_focused_trip] = useState(null);
  const [selected_person, _set_selected_person] = useState(null);
  const [selected_trip, _set_selected_trip] = useState(null);
  const [touchmove_enabled, _set_touchmove_enabled] = useState(true);
  const [[people_order, people_order_descr], _set_people_order] = useState([default_people_sorting, "first_trip"]);
  const [trip_initial_anim_delays, _] = useState([...Array(trip_dates.length)].map(Math.random))

  // Use a ref for tracking whether mouseover events are enabled or disabled so
  // that it can be updated and read from synchronously
  const animating = useRef(false);

  // ***************************************************************************
  // * Compute trip and person_trip data, the bulk of which involves
  // * calculating all the angles.
  // ***************************************************************************
  const [segment_gap, people, trips] = useMemo(() => {
    const SELECTED_PERSON_ZOOM_FACTOR = 2;
    // Compute num_person_trips and num_nontrivial_people
    let num_person_trips = 0;
    let num_nontrivial_people = 0;
    for (let i = 0; i < data_names.length; i++) {
      // - i is the position of the person around the circle
      // - person_column is the column in data_names/data_binary for that person
      // - person_index is the index by which the people are coloured (which, in turn,
      //     is given by the first trip).
      const person_column = people_order[i];
      const person_index = default_people_sorting.indexOf(person_column);
      let trips_for_this_person = 0;
      for (let j = 0; j <  data_binary.length; j++) {
        trips_for_this_person += (data_binary[j][people_order[i]] !== 0);
      }
      const zoom_factor = person_index === selected_person ? SELECTED_PERSON_ZOOM_FACTOR : 1;
      num_person_trips += trips_for_this_person * zoom_factor;
      num_nontrivial_people += (trips_for_this_person !== 0);
    }

    const PERSON_TRIP_TO_GAP_RATIO = 2;
    const num_segments = PERSON_TRIP_TO_GAP_RATIO * num_person_trips + num_nontrivial_people;
    const angle_per_segment = 2 * Math.PI / num_segments;
    const angle_per_person_trip = PERSON_TRIP_TO_GAP_RATIO * angle_per_segment;
    // Start just past 12 o'clock.
    let angle_cumulutive = angle_per_segment / 2;
    let people = {};
    let trips = {};
    for (let i = 0; i < data_names.length; i++) {
      // For explanations of what i, person_column and person_index are, see above.
      const person_column = people_order[i];
      const person_index = default_people_sorting.indexOf(person_column);
      let person_start_angle = angle_cumulutive;
      let trips_this_person = [];
      let group_size = null;
      for (let j = 0; j < data_binary.length; j++) {
        if (data_binary[j][person_column]) {
          //
          // Create person_trip with person person_index and trip j
          //
          if (group_size !== null && data_binary[j][person_column] !== group_size) {
            console.log(`WARNING: Inconsistent group size under ${data_names[person_column]}`);
          }
          group_size = data_binary[j][person_column];
          // Compute start and end angle for this person_trip
          const start_angle = angle_cumulutive;
          const zoom_factor = person_index === selected_person ? SELECTED_PERSON_ZOOM_FACTOR : 1;
          angle_cumulutive += angle_per_person_trip * zoom_factor;
          const end_angle   = angle_cumulutive;

          // Store person_trip object
          let person_trip = {
            person_index: person_index,
            trip_index : j,
            start_angle : start_angle,
            end_angle : end_angle
          };
          trips_this_person.push(person_trip);

          if (j in trips) {
            // Add this person_trip to existing trip
            trips[j].person_trips.push(person_trip);
          } else {
            // Create new trip object with this person_trip
            trips[j] = {
              trip_date : trip_dates[j],
              trip_key_memory : trip_key_memories[j],
              person_trips : [person_trip]
            };
          }

          // Add pointer to trip into person_trip
          person_trip.trip = trips[j];
        }

      }

      // If non-trivial person, record it here
      if (trips_this_person.length > 0) {
        people[person_index] = {
          person_index : person_index,
          name : data_names[person_column],
          start_angle : person_start_angle,
          end_angle : angle_cumulutive,
          person_trips : trips_this_person,
          group_size : group_size
        };
        for (let pt in trips_this_person) {
          trips_this_person[pt].person = people[person_index];
        }

        // Insert gap before next person
        angle_cumulutive += angle_per_segment;
      }

    }

    return [angle_per_segment, people, trips]
  }, [data_binary, data_names, people_order, trip_dates, trip_key_memories, default_people_sorting, selected_person]);

  // ***************************************************************************
  // * Custom state updaters
  // ***************************************************************************
  const set_focused_person = useCallback((person_index) => {
    if (!animating.current) {
      _set_focused_person(person_index);
      _set_focused_trip(null);
    }
  }, []);

  const set_focused_trip = useCallback((trip_index) => {
    if (!animating.current) {
      _set_focused_person(null);
      _set_focused_trip(trip_index);
    }
  }, []);

  const set_selected_person = useCallback((person_index) => {
    _set_selected_person(person_index);
    _set_selected_trip(null);
    // Set focused person/trip too
    _set_focused_person(person_index);
    _set_focused_trip(null);
  }, []);

  const set_selected_trip = useCallback((trip_index) => {
    _set_selected_trip(trip_index);
    _set_focused_trip(trip_index);
    // Only deselect person if they weren't on this trip
    let selected_person_on_this_trip = false;
    const trip = trips[trip_index];
    for (let i in trip.person_trips) {
      if (trip.person_trips[i].person_index === selected_person) {
        selected_person_on_this_trip = true;
      }
    }
    if (!selected_person_on_this_trip) {
      _set_selected_person(null);
      _set_focused_person(null);
    }
  }, [selected_person, trips]);

  const clear_selection = useCallback(() => {
    _set_selected_person(null);
    _set_selected_trip(null);
    _set_focused_trip(null);
    _set_focused_person(null);
  }, []);

  const update_people_order = useCallback((ordering_data, ordering_desc) => {
    const new_order = get_people_ordering([ordering_data], people_order)
    _set_people_order([new_order, ordering_desc]);
  }, [get_people_ordering, people_order]);

  // Touch-related event handlers
  const on_touch_move = useCallback((e) => {
    if (e.touches.length > 1) {
      // Disable touchmove if we're panning or zooming
      _set_touchmove_enabled(false);
      return;
    }
    // If touchmove disabled or currently animating, don't do anything
    if (!touchmove_enabled || animating.current) {
      return;
    }
    const elem = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    if (!elem) {
      return;
    }
    if ("trip_index" in elem.dataset) {
      set_focused_trip(parseInt(elem.dataset.trip_index));
    } else if ("person_index" in elem.dataset) {
      set_focused_person(parseInt(elem.dataset.person_index));
    }
  }, [touchmove_enabled]);

  const on_touch_end = useCallback((e) => {
    if (e.touches.length === 0) {
      // If all touch points removed from the screen, re-enable touchmove
      _set_touchmove_enabled(true);

      // Make focused person/trip selected
      if (focused_person !== null) {
        set_selected_person(focused_person);
      } else if (focused_trip !== null) {
        set_selected_trip(focused_trip);
      }
    }
  }, [focused_person, focused_trip]);

  // Layout-related
  const radius = 450 - MARGIN;
  // Trips bar
  const trips_bar_offset = MARGIN+10;
  const trips_bar_translate_str = (orient === "portrait") ?
                                  `translate(0, ${radius + trips_bar_offset})` :
                                  `translate(${radius + trips_bar_offset}, 0)`;

  // Highlighted person/trip
  let highlighted_person = null;
  let highlighted_trip = null;
  if (focused_person !== null || focused_trip !== null) {
    highlighted_person = focused_person;
    highlighted_trip = focused_trip;
  } else {
    // It's possible to have both a person and a trip selected at the same time,
    // so be a bit careful with highlighting here. selected_trip takes precedence.
    if (selected_trip !== null) {
      highlighted_trip = selected_trip;
    } else {
      highlighted_person = selected_person;
    }
  }

  // Hammock diary url
  let hammock_diary_url = '';
  if (focused_trip !== null) {
    hammock_diary_url = hammock_diary_urls[focused_trip];
  } else if (selected_trip !== null) {
    hammock_diary_url = hammock_diary_urls[selected_trip];
  }

  const prev_selected_person = usePrevious(selected_person);
  const selected_person_change = selected_person !== prev_selected_person;

  return (
    <div className="full_screen_app_container"
         onClick={clear_selection}>
      <div id="svg_container" className={orient}>
        <svg viewBox={`0 0 ${width} ${height}`}
             style={{fontSize: 11}}
             onTouchMove={on_touch_move}
             onTouchEnd={on_touch_end}>
          <g transform={`translate(${centre_x}, ${centre_y})`}>
            <g id="trips_bar" transform={trips_bar_translate_str}>
              <TripsBar orient={orient}
                        trips={trips}
                        num_trips={data_binary.length}
                        hammock_diary_urls={hammock_diary_urls}
                        length={2*radius + 50}
                        highlighted_trip={highlighted_trip}
                        highlighted_person={highlighted_person}
                        selected_trip={selected_trip}
                        selected_person={selected_person}
                        trip_initial_anim_delays={trip_initial_anim_delays}
                        set_focused_trip={set_focused_trip}
                        set_selected_trip={set_selected_trip}/>
            </g>
            <g id="nodes_labels"><NodesLabels people={people}
                                              num_people={data_names.length}
                                              radius={radius}
                                              radius_off_screen={radius_off_screen}
                                              highlighted_person={highlighted_person}
                                              highlighted_trip={highlighted_trip}
                                              segment_gap={segment_gap}
                                              selected_person_change={selected_person_change}
                                              set_focused_person={set_focused_person}
                                              set_selected_person={set_selected_person}
                                              animating={animating}/></g>
            <g id="trip_ribbons"><TripRibbons num_trips={data_binary.length}
                                              trips={trips}
                                              radius={radius}
                                              highlighted_trip={highlighted_trip}
                                              highlighted_person={highlighted_person}
                                              selected_person_change={selected_person_change}
                                              initial_anim_delays={trip_initial_anim_delays}
                                              set_focused_trip={set_focused_trip}
                                              set_selected_trip={set_selected_trip}/></g>
            <g id="trip_caption"><TripCaption trip={highlighted_trip !== null ? trips[highlighted_trip] : null}/></g>
            <g id="person_caption"><PersonCaption person={highlighted_person !== null ? people[highlighted_person] : null}/></g>
          </g>
        </svg>
      </div>
      <div id="non_svg_container">
        <div className="slide_in_container" style={{marginTop : order_buttons_margin}}>
          <div className="order_buttons slide_in">
            <button id="order_button_intro">
              Order by:
            </button>
            <button className="order_button" onClick={no_propagate(() => update_people_order(first_trip_data, "first_trip"))}
                    disabled={people_order_descr === "first_trip"}>
              first trip
            </button>
            <button className="order_button" onClick={no_propagate(() => update_people_order(num_trips_data, "num_trips"))}
                    disabled={people_order_descr === "num_trips"}>
              number of trips
            </button>
            <button className="order_button" onClick={no_propagate(() => update_people_order(num_covoyagers_data, "num_covoyagers"))}
                    disabled={people_order_descr === "num_covoyagers"}>
              number of co-voyagers
            </button>
          </div>
        </div>
        <HammockDiaryLink url={hammock_diary_url}/>
      </div>
    </div>
  );
};

export default ChordDiagram;
